import React, { useState, useEffect, useCallback } from 'react';
import { useApi } from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { HandCoins, Sparkles, ChevronDown, ChevronUp } from 'lucide-react';

const C = { navy: '#0D1B2A', blue: '#1A4C7C', teal: '#00B4D8', green: '#1D9E75', red: '#E24B4A', amber: '#E8A020', slate: '#5C6B7A' };
const fmt$ = (v) => `$${Math.round(v || 0).toLocaleString()}`;

const STATUS = {
  identified: { color: C.slate, label: 'Identified' },
  demand_sent: { color: C.blue, label: 'Demand Sent' },
  negotiating: { color: C.amber, label: 'Negotiating' },
  agreed: { color: C.teal, label: 'Agreed' },
  partially_recovered: { color: C.teal, label: 'Partially Recovered' },
  recovered: { color: C.green, label: 'Recovered' },
  abandoned: { color: C.red, label: 'Abandoned' },
};
const NEXT_ACTIONS = {
  identified: [['send_demand', 'Send Demand'], ['negotiate', 'Negotiate'], ['agree', 'Agree']],
  demand_sent: [['negotiate', 'Negotiate'], ['agree', 'Agree']],
  negotiating: [['agree', 'Agree']],
  agreed: [['record_receipt', 'Record Receipt']],
  partially_recovered: [['record_receipt', 'Record Receipt']],
};

const Overline = ({ children }) => (
  <p className="text-[10px] uppercase tracking-[0.15em]" style={{ color: C.slate, fontFamily: 'Georgia, serif' }}>{children}</p>
);

function RecoveryRow({ r, onAction, busy }) {
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState('');
  const st = STATUS[r.status] || STATUS.identified;
  const target = r.agreed_amount || r.expected_amount || 0;
  const actions = NEXT_ACTIONS[r.status] || [];
  const isOpen = !['recovered', 'abandoned'].includes(r.status);

  return (
    <div className="border-b" style={{ borderColor: 'rgba(136,150,168,0.1)' }} data-testid={`recovery-row-${r.claim_reference}`}>
      <button onClick={() => setOpen(o => !o)} className="w-full grid grid-cols-12 gap-2 items-center px-4 py-3 text-left hover:bg-[#F5F7FA]">
        <div className="col-span-3">
          <span className="font-mono text-xs" style={{ color: C.navy }}>{r.claim_reference}</span>
          <p className="text-[10px] truncate" style={{ color: C.slate }}>{r.business_name}</p>
        </div>
        <div className="col-span-2 text-xs capitalize" style={{ color: C.slate }}>{(r.recovery_type || '').replace('_', ' ')}<p className="text-[10px] truncate">{r.debtor || '—'}</p></div>
        <div className="col-span-2"><span className="px-2 py-0.5 rounded-full text-[10px] font-semibold" style={{ background: `${st.color}12`, color: st.color }}>{st.label}</span>
          {r.days_open != null && isOpen && <span className="text-[10px] ml-1.5" style={{ color: r.days_open > 60 ? C.red : C.slate }}>{r.days_open}d</span>}</div>
        <div className="col-span-2 text-right font-mono text-xs" style={{ color: C.navy }}>{fmt$(target)}</div>
        <div className="col-span-2 text-right font-mono text-xs" style={{ color: C.green }}>{fmt$(r.recovered_amount)}</div>
        <div className="col-span-1 flex justify-end">{open ? <ChevronUp className="w-3.5 h-3.5" style={{ color: C.slate }} /> : <ChevronDown className="w-3.5 h-3.5" style={{ color: C.slate }} />}</div>
      </button>
      {open && (
        <div className="px-4 pb-4 space-y-3">
          {isOpen && (
            <div className="flex items-center gap-2 flex-wrap">
              {actions.map(([a, label]) => (
                <Button key={a} size="sm" disabled={busy}
                  onClick={() => onAction(r, a, (a === 'agree' || a === 'record_receipt') ? Number(amount) || null : null)}
                  className="text-[11px] rounded-sm text-white h-7" style={{ background: a === 'record_receipt' ? C.green : C.blue }}
                  data-testid={`recovery-action-${a}-${r.claim_reference}`}>{label}</Button>
              ))}
              {(r.status === 'negotiating' || r.status === 'agreed' || r.status === 'partially_recovered' || r.status === 'identified' || r.status === 'demand_sent') && (
                <input type="number" min="0" value={amount} onChange={e => setAmount(e.target.value)} placeholder="$ amount"
                  className="text-xs px-2 py-1.5 rounded-sm bg-white outline-none w-28" style={{ border: '1px solid rgba(136,150,168,0.3)', color: C.navy }}
                  data-testid={`recovery-amount-${r.claim_reference}`} />
              )}
              <Button size="sm" variant="outline" disabled={busy} onClick={() => onAction(r, 'abandon', null)}
                className="text-[11px] rounded-sm h-7" style={{ color: C.red, borderColor: `${C.red}40` }}
                data-testid={`recovery-action-abandon-${r.claim_reference}`}>Abandon</Button>
            </div>
          )}
          <div className="space-y-1">
            {(r.actions || []).slice().reverse().map((a, i) => (
              <p key={i} className="text-[11px]" style={{ color: C.slate }}>
                <span className="font-medium capitalize" style={{ color: C.navy }}>{a.action.replace(/_/g, ' ')}</span>
                {a.amount != null && <> · {fmt$(a.amount)}</>}
                {a.notes && <> — {a.notes}</>} · {a.by} · {new Date(a.at).toLocaleDateString('en-AU', { day: '2-digit', month: 'short' })}
              </p>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function RecoveriesPage() {
  const api = useApi();
  const [data, setData] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get('/claims/recoveries/summary').then(r => setData(r.data)).catch(() => {});
    api.get('/claims/recoveries/candidates').then(r => setCandidates(r.data.candidates)).catch(() => {});
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const doAction = async (r, action, amount) => {
    setBusy(true);
    try {
      await api.put(`/claims/recoveries/${r.recovery_id}/action`, { action, amount, notes: '' });
      toast.success(`${r.claim_reference} — ${action.replace(/_/g, ' ')} recorded`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Action failed'); }
    setBusy(false);
  };

  const openRecovery = async (c) => {
    setBusy(true);
    try {
      await api.post(`/claims/${c.claim_id}/recoveries`, {
        recovery_type: c.suggested_type, debtor: '', expected_amount: c.suggested_amount,
        notes: c.signals.length ? `ARIA signals: ${c.signals.join(', ')}` : 'ARIA candidate sweep', source: 'aria',
      });
      toast.success(`Recovery opened on ${c.claim_reference} — expecting ${fmt$(c.suggested_amount)}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not open recovery'); }
    setBusy(false);
  };

  if (!data) return <div className="space-y-3">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-24 rounded-sm" />)}</div>;

  return (
    <div className="space-y-5" data-testid="recoveries-page">
      <div>
        <h2 className="text-xl font-medium tracking-tight" style={{ color: C.navy }}>Recoveries & Subrogation</h2>
        <p className="text-sm mt-0.5" style={{ color: C.slate }}>Track every dollar owed by third parties — from demand to receipt — so recoverable money never slips away.</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[['Open recoveries', data.open_count, C.navy, data.stale_count ? `${data.stale_count} stale (60+ days)` : 'none stale'],
          ['Outstanding', fmt$(data.expected_outstanding), C.amber, 'expected, not yet received'],
          ['Recovered to date', fmt$(data.recovered_total), C.green, 'receipts booked against claims'],
          ['Success rate', data.success_rate != null ? `${data.success_rate}%` : '—', C.blue, 'of closed recoveries won']].map(([l, v, color, d]) => (
          <Card key={l} className="p-4 rounded-sm bg-white" style={{ border: '1px solid rgba(136,150,168,0.2)' }}>
            <Overline>{l}</Overline>
            <p className="text-3xl font-light tracking-tight mt-1" style={{ color, fontVariantNumeric: 'tabular-nums' }}>{v}</p>
            <p className="text-[10px] mt-1" style={{ color: C.slate }}>{d}</p>
          </Card>
        ))}
      </div>

      {candidates.length > 0 && (
        <Card className="p-5 rounded-sm bg-white" style={{ border: '1px solid rgba(0,180,216,0.35)', borderLeft: `4px solid ${C.teal}` }} data-testid="recovery-candidates">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles className="w-4 h-4" style={{ color: C.teal }} />
            <Overline>ARIA found {candidates.length} settled claims with recovery potential</Overline>
          </div>
          <div className="space-y-2">
            {candidates.slice(0, 6).map(c => (
              <div key={c.claim_id} className="flex items-center justify-between gap-3 flex-wrap py-1.5 border-b last:border-0" style={{ borderColor: 'rgba(136,150,168,0.08)' }}>
                <div className="min-w-0">
                  <span className="font-mono text-xs" style={{ color: C.navy }}>{c.claim_reference}</span>
                  <span className="text-xs ml-2 capitalize" style={{ color: C.slate }}>{c.claim_type} · paid {fmt$(c.paid_amount)}</span>
                  {c.signals.length > 0 && <span className="ml-2 px-1.5 py-0.5 rounded text-[10px] font-medium" style={{ background: `${C.teal}12`, color: C.teal }}>{c.signals.join(' · ')}</span>}
                </div>
                <Button size="sm" disabled={busy} onClick={() => openRecovery(c)} className="text-[11px] rounded-sm text-white h-7" style={{ background: C.teal }} data-testid={`recovery-open-${c.claim_reference}`}>
                  Open — expect {fmt$(c.suggested_amount)}
                </Button>
              </div>
            ))}
          </div>
        </Card>
      )}

      <Card className="rounded-sm bg-white" style={{ border: '1px solid rgba(136,150,168,0.2)' }} data-testid="recovery-tracker">
        <div className="grid grid-cols-12 gap-2 px-4 py-2.5 text-[10px] uppercase tracking-wider border-b" style={{ color: C.slate, borderColor: 'rgba(136,150,168,0.15)' }}>
          <span className="col-span-3 font-semibold">Claim</span>
          <span className="col-span-2 font-semibold">Type / Debtor</span>
          <span className="col-span-2 font-semibold">Status</span>
          <span className="col-span-2 font-semibold text-right">Target</span>
          <span className="col-span-2 font-semibold text-right">Received</span>
          <span className="col-span-1" />
        </div>
        {data.items.length === 0 ? (
          <div className="py-10 text-center">
            <HandCoins className="w-7 h-7 mx-auto mb-2" style={{ color: C.slate }} />
            <p className="text-sm" style={{ color: C.slate }}>No recoveries yet — open one from ARIA's candidates above.</p>
          </div>
        ) : data.items.map(r => <RecoveryRow key={r.recovery_id} r={r} onAction={doAction} busy={busy} />)}
      </Card>
    </div>
  );
}
